package com.neurobs.ccab

import com.neurobs.ccab.analysis.TestAnalysis.transcriptTestIdens
import com.neurobs.ccab.db.{FileTranscriptData, FileTranscriptManager, FileTranscriptRequestData, FileTranscriptResponseData, LogfileData, ReviewedTestTranscriptData, ReviewedTestTranscriptManager, SpeechAudioFileData, SpeechAudioFileManager, TestManager, TestRunData, TestRunManager, TestSessionManager, TestTranscriptData, TestTranscriptManager}
import com.neurobs.errors._
import com.neurobs.util.{Guid, PresentationEncryptedData}

import scala.concurrent.duration.Duration
import scala.concurrent.{Await, ExecutionContext, Future}

/**
 * Handles reporting of transcript status of sessions and studies, and
 * creation of transcript requests to start processing of missing transcripts
 */
object TranscriptSyncManager {
	val asrEngineIds = Seq[Long](1, 2, 3, 4, 5, 6)

	case class AudioFileEngineStatus
	(
		engineId: Long,
		requests: Seq[FileTranscriptRequestData],
		responses: Seq[FileTranscriptResponseData],
		transcripts: Seq[FileTranscriptData]
	) {
		def complete: Boolean = transcripts.nonEmpty
		def withoutTranscripts: AudioFileEngineStatus = copy(transcripts = transcripts.map(_.copy(transcript = "...")))
	}

	case class AudioFileStatus
	(
		localFilename: String,
		s3Storage: Option[String],
		speechAudioFile: Option[SpeechAudioFileData],
		engines: Seq[AudioFileEngineStatus]
	) {
		def complete(asrEngineIds: Seq[Long]): Boolean = asrEngineIds.forall(id => engines.exists(s => s.engineId == id && s.complete))
		def completeStatus(asrEngineIds: Seq[Long]): String = {
			if (s3Storage.isEmpty) s"Audio file $localFilename not in S3"
			else if (speechAudioFile.isEmpty) s"Speech audio file for $localFilename not created"
			else asrEngineIds.find(id => !engines.exists(_.engineId == id)) match {
				case Some(id) => s"Engine $id (at least) not requested for audio file $localFilename"
				case None =>
					engines.find(!_.complete) match {
						case Some(e) => s"Engine ${e.engineId} request failed for audio file $localFilename"
						case None => s"This is a bug"
					}
			}
		}
		def withoutTranscripts: AudioFileStatus = copy(engines = engines.map(_.withoutTranscripts))
	}

	case class TestRunSyncStatus
	(
		testRun: TestRunData,
		logfile: LogfileData,
		isTranscriptTest: Boolean,
		language: Option[String],
		isLanguageSupported: Boolean,
		s3Storage: Option[String] = None,
		recordings: Seq[AudioFileStatus] = Seq(),
		testTranscripts: Seq[TestTranscriptData] = Seq(),
		reviewedTranscripts: Seq[ReviewedTestTranscriptData] = Seq(),
		transcriptS3Storage: Option[String] = None
	) {
		def transcriptPending: Boolean = isTranscriptTest && isLanguageSupported && transcriptS3Storage.isEmpty
		def transcriptSyncedStatus(asrEngineIds: Seq[Long]): String = {
			if (!transcriptPending) "Complete"
			else recordings.find(!_.complete(asrEngineIds)) match {
				case Some(r) => r.completeStatus(asrEngineIds)
				case None =>
					if (testTranscripts.isEmpty) "Test transcript not generated"
					else if (testTranscripts.exists(_.error.nonEmpty)) "Test transcript generation had error"
					else if (transcriptS3Storage.isEmpty) "Transcript not synced to S3"
					else "This is a bug"
			}
		}
		def withoutTranscripts: TestRunSyncStatus = copy(recordings = recordings.map(_.withoutTranscripts), testTranscripts = testTranscripts.map(t => t.copy(transcript = t.transcript.map(_ => "..."))), reviewedTranscripts = reviewedTranscripts.map(_.copy(transcript = "...")))
	}

	def testrunStatus(logfileFilename: String, testRunManager: TestRunManager, testTranscriptManager: TestTranscriptManager, reviewedTestTranscriptManager: ReviewedTestTranscriptManager, speechAudioFileManager: SpeechAudioFileManager, fileTranscriptManager: FileTranscriptManager, testManager: TestManager, storage: FileStorage, dataStorage: CCABDataStorage)(implicit ec: ExecutionContext): Future[Result[TestRunSyncStatus]] = {

		def process(view: TestRunLogfileView): Future[Result[TestRunSyncStatus]] = {
			val logfileContentsOptResFut = dataStorage.getLogfile(view.logfileData.origFilename)
			val testTranscriptsFut = testTranscriptManager.allTranscriptsFromTestRunId(view.testRun.id)
			val reviewedFut = reviewedTestTranscriptManager.fromTestRunId(view.testRun.id)
			val speechAudioFilesFut = speechAudioFileManager.fromTestRunId(view.testRun.id)
			val requestsFut = fileTranscriptManager.fileTranscriptRequestFromTestRunId(view.testRun.id)
			val responsesFut = fileTranscriptManager.fileTranscriptResponseFromTestRunId(view.testRun.id)
			val fileTranscriptsFut = fileTranscriptManager.fromTestRunId(view.testRun.id)
			val testOptFut = testManager.fromId(view.testRun.testId)
			for {
				logfileContentsOptRes <- logfileContentsOptResFut
				testTranscripts <- testTranscriptsFut
				reviewed <- reviewedFut
				speechAudioFiles <- speechAudioFilesFut
				requests <- requestsFut
				responses <- responsesFut
				fileTranscripts <- fileTranscriptsFut
				testOpt <- testOptFut
			} yield {
				for {
					logfileContentsOpt <- logfileContentsOptRes
					logfileContents <- toResult(logfileContentsOpt, noValueMessage = s"Logfile not in DB for test run $logfileFilename")
				}
				yield {
					val paths = TestRunPaths(view.logfileData)
					val runS3Files = storage.connection().sessionDataFiles(paths.base, maxKeys = 100)
					val language = LogfileMetadata.extractStandardLanguage(logfileContents)
					val localAudioFilenames = Logfile.extractAudioFileCodes(logfileContents)
					val recordings = localAudioFilenames map { localFilename =>
						val path = paths.audioFilePath(localFilename)
						val speechAudioFile = speechAudioFiles.find(_.location == Some(path))
						val engines = speechAudioFile match {
							case None => Seq()
							case Some(saf) =>
								val engineIds = (requests.map(_.asrEngineId) ++ fileTranscripts.map(_.asrEngineId)).toSet.toSeq
								engineIds map { engineId =>
									val engineRequests = requests.filter(r => r.asrEngineId == engineId && r.speechAudioFileId == saf.id)
									val engineTranscripts = fileTranscripts.filter(t => t.asrEngineId == engineId && t.speechAudioFileId == saf.id)
									val engineResponses = responses.filter(r => engineRequests.exists(_.id == r.fileTranscriptRequestId))
									AudioFileEngineStatus(engineId, engineRequests, engineResponses, engineTranscripts)
								}
						}
						AudioFileStatus(localFilename, s3Storage = runS3Files.find(_ == path), speechAudioFile, engines)
					}

					TestRunSyncStatus(
						testRun = view.testRun,
						logfile = view.logfileData,
						isTranscriptTest = testOpt.exists(t => transcriptTestIdens.contains(t.identifier)),
						language = language,
						isLanguageSupported = language.forall(_ == "English"),
						s3Storage = runS3Files.find(_ == paths.logfilePath),
						recordings = recordings,
						testTranscripts = testTranscripts,
						reviewedTranscripts = reviewed,
						transcriptS3Storage = runS3Files.find(_ == paths.transcriptPath)
					)
				}
			}
		}

		testRunManager.testRunLogfileViewFromLogfileFilename(logfileFilename) flatMap {
			case None => Future.successful(errorResult(s"Test run for logfile '$logfileFilename' not found."))
			case Some(view) => process(view)
		}
	}

	/** Represents an audio file in the file store which has no entry in the
	 * casr.speech_audio_file table, and therefore no transcripts
	 *
	 * @param location Relative file path within the "sessions" folder of the file store
	 * @param testRunId The id of the associated test run
	 */
	case class UnregisteredAudioFile(location: String, testRunId: Long)

	/** Engine/file pairs for missing file transcripts
	 *
	 * @param asrEngineId The ASR engine id
	 * @param speechAudioFileId The speech audio file id
	 */
	case class MissingFileTranscriptRequest(asrEngineId: Long, speechAudioFileId: Long, testRunId: Long)

	/** Test transcripts that have failed and not subsequently succeeded
	 *
	 * @param testRunId
	 */
	case class ErrorTestTranscript(testRunId: Long)

	/** Data representing missing transcripts for a session
	 *
	 * @param missingTestTranscriptCount The number of test runs requiring test transcripts without one
	 * @param unregisteredFiles Audio file with no casr.speech_audio_file entry
	 * @param fileRequests Engine/file pairs for which to request file transcripts
	 */
	case class MissingSessionTranscriptData(sessionId: Long, subjectGuid: Guid, missingTestTranscriptCount: Int, unregisteredFiles: Seq[UnregisteredAudioFile], fileRequests: Seq[MissingFileTranscriptRequest], errorTestTranscripts: Seq[ErrorTestTranscript]) {
		def isEmpty: Boolean = (missingTestTranscriptCount == 0) && unregisteredFiles.isEmpty && fileRequests.isEmpty
	}

	/** Creates backlog entries in the casr.file_transcript_request table to produce missing
	 * transcripts
	 */
	def createRequestsForSession(sessionId: Long, sessions: TestSessionManager, runs: TestRunManager, speechAudioFiles: SpeechAudioFileManager, fileTranscripts: FileTranscriptManager, storage: FileStorage, tests: TestManager, casrServer: CasrServer, dataStorage: CCABDataStorage)(implicit ec: ExecutionContext): Future[Result[Unit]] = {
		sessionStatus(sessionId = sessionId, sessions, runs, speechAudioFiles, fileTranscripts, storage, tests) flatMap { status =>
			createRequestsForStatus(status, speechAudioFiles, fileTranscripts, tests, runs, dataStorage,  casrServer)
		}
	}

	def createRequestsForStatus(status: MissingSessionTranscriptData, speechAudioFiles: SpeechAudioFileManager, fileTranscripts: FileTranscriptManager, testManager: TestManager, runs: TestRunManager, dataStorage: CCABDataStorage, casrServer: CasrServer)(implicit ec: ExecutionContext): Future[Result[Unit]] = {
		val testRunIds = (status.unregisteredFiles.map(_.testRunId) ++ status.fileRequests.map(_.testRunId) ++ status.errorTestTranscripts.map(_.testRunId)).toSet
		TestRunMetadata.get(testRunIds, testManager, runs, dataStorage) flatMap {
			case Left(error) => Future.successful(Left(error))
			case Right(metadata) =>
				val unregistered: Future[Result[Unit]] = Future.sequence(status.unregisteredFiles map { f =>
					val filename = f.location.split("/").drop(1).mkString("/")
					val extras = metadata(f.testRunId)
					for {
						speechAudioFile <- speechAudioFiles.createWithAssociation(filename = filename, location = f.location, testRunId = f.testRunId)
						_ <- fileTranscripts.createBacklogRequests(speechAudioFile.id, asrEngineIds, language = extras.language, testIden = extras.testIden)
					} yield Unit
				}).map(_ => Right(Unit))

				val errorTests: Future[Result[Unit]] = Future.sequence(status.errorTestTranscripts map { t =>
						val extras = metadata(t.testRunId)
						casrServer.createTestTranscript(testRunId = t.testRunId, asrEngineIds = Some(asrEngineIds), language = extras.language, testIden = extras.testIden)._2.map(_.map(_ => ()))
					}).map(sequence(_).map(_ => Unit))

				val fileRequests: Future[Result[Unit]] = Future.sequence(
					status.fileRequests map { r =>
						val extras = metadata(r.testRunId)
						fileTranscripts.createBacklogRequest(speechAudioFileId = r.speechAudioFileId, asrEngineId = r.asrEngineId, language = extras.language, testIden = extras.testIden)
					}
				).map(_ => Right(Unit))

				for {
					unregisteredRes <- unregistered
					errorTestsRes <- errorTests
					fileRequestsRes <- fileRequests
				} yield for {
					_ <- unregisteredRes
					_ <- errorTestsRes
					_ <- fileRequestsRes
				} yield ()
		}
	}

	def studyStatus(studyId: Long, sessions: TestSessionManager, runs: TestRunManager, speechAudioFiles: SpeechAudioFileManager, fileTranscripts: FileTranscriptManager, storage: FileStorage, tests: TestManager)(implicit ec: ExecutionContext): Future[Seq[MissingSessionTranscriptData]] = {
		sessions.fromStudyId(studyId) map { sess =>
			val statuses = sess.map(s => Await.result(sessionStatus(s.id, sessions, runs, speechAudioFiles, fileTranscripts, storage, tests), Duration("15 seconds")))
			statuses.filter(!_.isEmpty)
		}
	}

	def sessionStatus(sessionId: Long, sessions: TestSessionManager, runs: TestRunManager, speechAudioFiles: SpeechAudioFileManager, fileTranscripts: FileTranscriptManager, storage: FileStorage, tests: TestManager)(implicit ec: ExecutionContext): Future[MissingSessionTranscriptData] = {
		def process(sessionOpt: Option[db.SessionData], noTranscriptRuns: Seq[TestRunLogfileView]): Future[MissingSessionTranscriptData] = {
			val subjectGuid = sessionOpt.map(_.subjectGuid).getOrElse(Guid.nullGuid)
			if (noTranscriptRuns.isEmpty || sessionOpt.isEmpty) Future.successful(MissingSessionTranscriptData(sessionId, subjectGuid, 0, Seq(), Seq(), Seq()))
			else {
				val subjectGuid = sessionOpt.get.subjectGuid
				val speechFilesFut = speechAudioFiles.fromSessionId(sessionId)
				val transcriptsFut = fileTranscripts.fromSessionId(sessionId)
				val requestsFut = fileTranscripts.backlogRequestsFromSessionId(sessionId)
				for {
					speechFiles <- speechFilesFut
					transcripts <- transcriptsFut
					requests <- requestsFut
				} yield {
					val sessionFiles = storage.connection().sessionDataFiles(subjectGuid, maxKeys = 5000).
						filter(file => file.endsWith(".wav") || file.endsWith(".flac"))
					val runFiles = for {
						r <- noTranscriptRuns
						logFilenameBase = r.logfileData.origFilename.substring(0, r.logfileData.origFilename.length - 4)
						f <- sessionFiles.filter(_.contains(logFilenameBase))
					} yield UnregisteredAudioFile(f, r.testRun.id)
					val unregistered = runFiles.filter(f => !speechFiles.exists(_._2.location.contains(f.location)))
					// exclude runs with no audio files in missing transcript runs
					val transcriptRequiredRuns = noTranscriptRuns.filter(r => runFiles.exists(_.testRunId == r.testRun.id))

					val missingRequests = for {
						f <- speechFiles
						engineId <- asrEngineIds if !transcripts.exists(t => t.asrEngineId == engineId && t.speechAudioFileId == f._2.id) && !requests.exists(r => r.asrEngineId == engineId && r.speechAudioFileId == f._2.id)
					} yield MissingFileTranscriptRequest(asrEngineId = engineId, speechAudioFileId = f._2.id, testRunId = f._1)

					val errorTestTranscripts = noTranscriptRuns.filter(r => !unregistered.exists(_.testRunId == r.testRun.id) && !missingRequests.exists(_.testRunId == r.testRun.id)).map(r => ErrorTestTranscript(r.testRun.id))

					MissingSessionTranscriptData(sessionId, subjectGuid, transcriptRequiredRuns.size, unregistered, missingRequests, errorTestTranscripts)
				}
			}
		}
		val sessionFut = sessions.fromId(sessionId)
		val hasTranscriptRunIdsFut = runs.hasTranscriptTestRunIdsFromSessionId(sessionId)
		val sessionRunsFut = runs.testRunLogfileViewFromSessionId(sessionId)
		val transcriptTestIdsFut = tests.transcriptTestIds
		for {
			session <- sessionFut
			hasTranscriptRunIds <- hasTranscriptRunIdsFut
			sessionRuns <- sessionRunsFut
			transcriptTestIds <- transcriptTestIdsFut
			noTranscriptRuns = sessionRuns filter { r =>
				transcriptTestIds.contains(r.testRun.testId) && !hasTranscriptRunIds.contains(r.testRun.id)
			}
			res <- process(session, noTranscriptRuns)
		} yield res
	}
}
